import './index.less';
import { apiService, AppInfo, AppWidgetStatus, ParseAppStatus } from '@/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { urlQuery, formatDate } from '@/shared/utils/tools';
import { AppListItem } from './AppListItem';
import { Loading, SvgIcon, Pagination, SelectOptionProps } from '@/components';
import { debounce } from 'lodash-es';
import Tabs, { TabPane } from 'rc-tabs';
import emptyImg from '@/assets/img/empty.svg';
import { useHistory } from 'react-router-dom';
import { AppTipModal } from './AppTipModal';
import { AppSearchBar } from './AppSearchBar';
import { GPAppUploadModal } from './GPAppUploadModal';
import { GAUploadModal } from './GAUploadModal';
import Cookies from 'js-cookie';
import { baseUrl } from '@/shared/utils';

export default function AppList() {
  const [tipModalVisible, setTipModalVisible] = useState<boolean>(false);
  const [currentApp, setCurrentApp] = useState<AppInfo>();
  const [loading, setLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const [data, setData] = useState<AppInfo[]>([]);
  const [appSearchOptions, setAppSearchOptions] = useState<SelectOptionProps[]>(
    [],
  );
  const { userId, slh, token } = urlQuery(); // 从url取的参数
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [state, setState] = useState<number>(ParseAppStatus.Inprocess);
  const [updateType, setUpdateType] = useState<number>(1);
  const [gpModalVisible, setGpModalVisible] = useState<boolean>(false);
  const [gaModalVisible, setGaModalVisible] = useState<boolean>(false);
  const history = useHistory();
  const { href } = window.location;
  const [isLogin, setIsLogin] = useState(sessionStorage.getItem('login') === '0' ? false : true);
  const defaultTimerValue = -1;
  const timerRef = useRef<NodeJS.Timeout | number | null>(defaultTimerValue);
  const loadData = async () => {
    setLoading(true);
    try {
      const pagedData = await apiService.getAppList({
        page: currentPage,
        keyword: keyword,
        updateType: updateType,
        pageSize: updateType === 2 ? 5 : 6,
      });
      const { total, appList } = pagedData;
      setIsLogin(true);
      sessionStorage.setItem('login', '1');
      setData(appList);
      if (updateType === 1 && appList.length === 0 && !keyword) {
        setGaModalVisible(true);
      }
      setTotal(total);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => {
    if (userId) {
      Cookies.set('devglobal_slh', decodeURIComponent(String(slh)));
      Cookies.set('userId', decodeURIComponent(String(userId)));
      Cookies.set('serviceToken', decodeURIComponent(String(token)));
      sessionStorage.setItem('login', '1');
    }
    history.push(`/`);
  }, [userId, slh, token]);

  const gotoAppDetail = (item: AppInfo) => {
    history.push(
      `/app-detail?appPkgName=${item.pkgName}&appVerCode=${item.currVer.verCode}&updateType=${updateType}`,
    );
    sessionStorage.setItem('appInfo', JSON.stringify(item));
  };

  /**
   * 应用历史版本(除当前版本)是否有小部件
   */
  const getAppHasWidgetBefore = (item: AppInfo) => {
    return item.previousWidgetStatus === AppWidgetStatus.Unchanged;
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, currentPage, updateType]);

  const handleSearch = (value: string) => {
    setKeyword(value.trim());
    setCurrentPage(1);
  };

  const handleKeywordChange = async (value: string) => {
    const keyword = value.trim();
    if (!keyword) {
      setAppSearchOptions([]);
      return;
    }
    try {
      const pagedData = await apiService.getAppList({
        page: currentPage,
        keyword: keyword,
        updateType: updateType,
        pageSize: updateType === 2 ? 5 : 6,
      });
      const { appList } = pagedData;
      setAppSearchOptions(
        appList.map((item: AppInfo) => ({
          key: item.pkgName,
          value: item.appName,
        })),
      );
    } catch (e) {}
  };

  const handleSearchSelect = (value: string) => {
    handleSearch(value);
    setAppSearchOptions([]);
  };

  const handleSearchFocus = () => {
    handleKeywordChange(keyword);
  };

  const handleGpUpdate = () => {
    loadData();
  }

  const handlePaginationChange = (current: number) => {
    setCurrentPage(current);
  };
  const clearTimerFn = () => {
    clearTimeout(timerRef.current as number);
    timerRef.current = defaultTimerValue;
  };
  const getAppStatusFn = useCallback(async (item) => {
    try {
      const res = await apiService.getAppStatus({
        abilityCode: item.abilityCode,
        abilityVersion: item.abilityVersion,
        appId: item.appId,
        appVersionName: item.appVersionName,
        appVersionCode: item.appVersionCode,
      });
      if (res && typeof res.state !== 'undefined') {
        // 0解析失败，1解析成功，2解析中
        setState(res.state);
        if (res.state === ParseAppStatus.Success) {
          gotoAppDetail(item);
        } else if (res.state === ParseAppStatus.Inprocess) {
          // 点击弹窗的时候handleAppItemClick()中 timerRef.current = null ;可以执行定时器
          // 关闭弹窗后clearTimerFn()中 timerRef.current还原为defaultTimerValue，不在执行定时器
          // 此处不用tipModalVisible判断因为 获取到的tipModalVisible是一直不变的
          if (timerRef.current === defaultTimerValue) return;
          timerRef.current = setTimeout(() => {
            getAppStatusFn(item);
          }, 1500);
        }
      } else {
        setState(ParseAppStatus.Fail);
      }
    } catch (error) {
      setState(ParseAppStatus.Fail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 小部件开放平台弹窗场景
  // https://xiaomi.f.mioffice.cn/docs/dock4zPJ9qooWelgpo5yJh8O7Pf
  const handleAppItemClick = (item: AppInfo) => {
    setCurrentApp(item);
    if (item.widgetStatus === AppWidgetStatus.None) {
      // 最新版本应用无小部件，查询状态是否正在解析中
      timerRef.current = null;
      getAppStatusFn(item);
      setTipModalVisible(true);
    } else {
      // 否则跳转详情页
      gotoAppDetail(item);
    }
  };

  const handleAppChange = (value: string) => {
    setUpdateType(parseInt(value));
  };

  const handleUploadGp = () => {
    setGpModalVisible(true);
  };

  const handleUploadGa = () => {
    window.open(baseUrl);
  };

  const login = () => {
    window.open(
      `${baseUrl}/login?platform=widget&redirectUrl=${encodeURIComponent(
        href,
      )}`,
    );
  };

  return (
    <section className="main-content-wrapper app-list-wrapper">
      <div className="main-content">
        <AppTipModal
          visible={tipModalVisible}
          state={state}
          closeLabel={
            currentApp && getAppHasWidgetBefore(currentApp)
              ? '管理当前小部件'
              : '返回'
          }
          onClose={() => {
            setTipModalVisible(false);
            clearTimerFn();
            if (currentApp && getAppHasWidgetBefore(currentApp)) {
              gotoAppDetail(currentApp);
            }
          }}
        />
        <GPAppUploadModal
          visible={gpModalVisible}
          closeLabel="返回"
          onClose={() => {
            setGpModalVisible(false);
          }}
          handleUpdate={handleGpUpdate}
        />
        <GAUploadModal
          visible={gaModalVisible}
          closeLabel="返回"
          onClose={() => {
            setGaModalVisible(false);
          }}
        />
        <div className="app-list__header">
          <span className="app-list__title">{window.trans('my-app')}</span>
          {isLogin && (
            <AppSearchBar
              onSearch={debounce(handleSearch, 300)}
              onChange={debounce(handleKeywordChange, 300)}
              onSelect={handleSearchSelect}
              onFocus={handleSearchFocus}
              autocompleteOptions={appSearchOptions}
            />
          )}
        </div>
        {isLogin && (
          <Tabs
            defaultActiveKey="1"
            className="app-list__exchange"
            onChange={handleAppChange}
          >
            <TabPane tab={window.trans('ga')} key="1"></TabPane>
            <TabPane tab={window.trans('gp')} key="2"></TabPane>
          </Tabs>
        )}
        {isLogin && (
          <div>
            {loading ||
            (Array.isArray(data) && data.length > 0) ||
            (updateType === 2 && !keyword) ? (
              <Loading loading={loading}>
                <div className="app-list__main">
                  {updateType === 2 && (
                    <div
                      className="app-list-item newapp"
                      onClick={handleUploadGp}
                    >
                      <div>
                        <span>+</span>
                        <span>{window.trans('add-app')}</span>
                      </div>
                    </div>
                  )}
                  {Array.isArray(data) &&
                    data.map((item) => (
                      <AppListItem
                        key={item.appId}
                        data={item}
                        onClick={() => {
                          handleAppItemClick(item);
                        }}
                      />
                    ))}
                </div>
                <Pagination
                  // defaultPageSize={pageSize}
                  total={total}
                  onChange={handlePaginationChange}
                />
              </Loading>
            ) : (
              <div className="app-list__empty">
                <img src={emptyImg} alt="" />
                <br />
                <span
                  onClick={updateType === 2 ? handleUploadGp : handleUploadGa}
                  className="go-upload-btn"
                >
                  { window.trans('no-app-upload')}
                  <SvgIcon name="collapse" />
                </span>
              </div>
            )}
          </div>
        )}
        {!isLogin && (
          <div className="app-list__login">
            <div className="login-desc">{window.trans('login-to-see')}</div>
            <div className="login-button" onClick={login}>
              {window.trans('go-login')}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
